import {ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import {skipToken} from '@reduxjs/toolkit/query';

import {useCallback, useRef, useState} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isEmpty, isNil, isNotNil, take, uniq} from 'ramda';

import {
  AuctionVehiclesFilterRequestBody,
  GetAuctionVehicleListApiArg,
  ListAuctionVehiclesByIdsApiArg,
  patchAuctionVehiclesInCaches,
  sourcingAuctionApi,
  useGetSourcingAuctionListQuery,
} from '@dms/api/core';

import {useSourcingIntervalPoller} from '../../../hooks/useSourcingIntervalPoller';

// Backend limit of `list-by-ids`. See README §Real-time polling.
const MAX_POLL_AD_IDS = 1000;

type UseAuctionsVehicleListQueriesProps = {
  filterBody: AuctionVehiclesFilterRequestBody | null;
  orderBy: GetAuctionVehicleListApiArg['orderBy'];
  currency: GetAuctionVehicleListApiArg['currency'];
  buyerCountry: ListAuctionVehiclesByIdsApiArg['buyerCountry'];
  pricingStrategy: GetAuctionVehicleListApiArg['pricingStrategy'];
  isFiltersOpen: boolean;
  isRealTime: boolean;
  page: number;
  pollingInterval: number;
  skip?: boolean;
};

export function useAuctionsVehicleListQueries(props: UseAuctionsVehicleListQueriesProps) {
  const {data, currentData, isLoading, isFetching, isError, error} = useGetSourcingAuctionListQuery(
    isNotNil(props.filterBody)
      ? {
          body: props.filterBody,
          page: props.page,
          orderBy: props.orderBy,
          currency: props.currency,
          pricingStrategy: props.pricingStrategy,
        }
      : skipToken,
    {skip: props.skip || props.isFiltersOpen}
  );

  // eslint-disable-next-line -eslint/no-explicit-any -- concrete RootState lives in the consuming app; matches the State/Extra typing used by `sourcingApiHelpers.ts` and `handleIncomingData.ts`.
  const dispatch = useDispatch<ThunkDispatch<any, any, UnknownAction>>();
  // eslint-disable-next-line -eslint/no-explicit-any -- same RootState reasoning as the dispatch type above.
  const store = useStore<any>();
  const [pollingFulfilledTimeStamp, setPollingFulfilledTimeStamp] = useState<number | undefined>(
    undefined
  );
  const [isPolling, setIsPolling] = useState(false);

  const isPollInFlightRef = useRef(false);

  const {filterBody, currency, buyerCountry, pricingStrategy, orderBy} = props;

  const reset = () => {
    if (isNil(filterBody)) {
      return;
    }

    const firstPageQueryArgs: GetAuctionVehicleListApiArg = {
      body: filterBody,
      page: 1,
      orderBy,
      currency,
      pricingStrategy,
    };

    dispatch(
      sourcingAuctionApi.util.updateQueryData(
        'getSourcingAuctionList',
        firstPageQueryArgs,
        (draft) => {
          draft.data = [];
          draft.links = {
            ...draft.links,
            offset: 0,
            size: 0,
          };
          draft.scrollAttemptedOffset = undefined;
          draft.isEndReached = undefined;
        }
      )
    );
    dispatch(
      sourcingAuctionApi.endpoints.getSourcingAuctionList.initiate(firstPageQueryArgs, {
        forceRefetch: true,
        subscribe: false,
      })
    );
  };

  const canPoll =
    !props.skip &&
    !props.isFiltersOpen &&
    props.isRealTime &&
    isNotNil(filterBody) &&
    isNotNil(currency);

  // useCallback for correctness (poller interval dep), not perf.
  const doPoll = useCallback(async () => {
    // A slow tick must finish before the next one starts.
    if (isNil(filterBody) || isNil(currency) || isPollInFlightRef.current) {
      return;
    }
    const mainCacheKeyArgs: GetAuctionVehicleListApiArg = {
      body: filterBody,
      page: 1,
      orderBy,
      currency,
      pricingStrategy,
    };
    const cachedItems =
      sourcingAuctionApi.endpoints.getSourcingAuctionList.select(mainCacheKeyArgs)(store.getState())
        .data?.data ?? [];
    const loadedAdIds = take(
      MAX_POLL_AD_IDS,
      uniq(cachedItems.map((item) => item.auctionVehicle.adId).filter(isNotNil))
    );
    if (isEmpty(loadedAdIds)) {
      return;
    }

    const pricingContext = {currency, buyerCountry, pricingStrategy};
    const requestedAt = Date.now();
    isPollInFlightRef.current = true;
    setIsPolling(true);
    const subscription = dispatch(
      sourcingAuctionApi.endpoints.listAuctionVehiclesByIds.initiate(
        {...pricingContext, body: {adIds: loadedAdIds}},
        {forceRefetch: true}
      )
    );
    try {
      const {data: auctionVehicles} = await subscription.unwrap();
      patchAuctionVehiclesInCaches(auctionVehicles, pricingContext, requestedAt, {
        dispatch,
        getState: store.getState,
      });
      setPollingFulfilledTimeStamp(Date.now());
    } catch {
      // Skip the tick silently and keep "Last updated"; the next tick retries.
    } finally {
      subscription.unsubscribe();
      isPollInFlightRef.current = false;
      setIsPolling(false);
    }
  }, [dispatch, store, filterBody, currency, buyerCountry, pricingStrategy, orderBy]);

  useSourcingIntervalPoller({
    enabled: canPoll,
    pollingInterval: props.pollingInterval,
    doPoll,
    resetKey: JSON.stringify({filterBody, currency, buyerCountry, pricingStrategy, orderBy}),
  });

  return {
    fulfilledTimeStamp: pollingFulfilledTimeStamp,
    isPolling,
    data,
    currentData,
    scrollAttemptedOffset: currentData?.scrollAttemptedOffset,
    isEndReached: currentData?.isEndReached,
    reset,
    isLoading,
    isFetching,
    isError,
    error,
  };
}
